// /onboarding/business/branches — «عندك أكثر من فرع؟» (board 26
// business/enroll/branches--v2): asked after «في أي مدينة؟» only when the
// platform's global `business.branches` is on (rulings R11/R12). The switch
// «نعم، أكثر من فرع» is off by default; on, «اسم الفرع الأول» is required
// (1..60, trimmed). The answer lives in the wizard's memory; ensureBusiness
// writes it onto profile's main branch once the business exists. The drawn
// «عنوانه» is the next screen («ما عنوان النشاط؟»). States: default ·
// validation.

import { useEffect, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { Toggle } from '@/components/Form';
import { useLayout } from '@/lib/layout';
import { BigInput, Msg } from '../components/widgets';
import { useBranchesQuestion, Wizard } from '../components/Wizard';
import { BRANCHES_Q, BUSINESS } from '../copy';
import { ONB } from '../data/flow';
import { onboardingStore, useOnboarding } from '../state';
import { ContinueAction, useBusinessGuard, useBusinessPanel } from './Business';

const NAME_MAX = 60;

export function BranchesScreen() {
  const guard = useBusinessGuard('branches');
  const navigate = useNavigate();
  const mobile = useLayout() === 'phone';
  const { branches } = useOnboarding();
  const question = useBranchesQuestion();
  const panel = useBusinessPanel('branches');
  const [error, setError] = useState<string | null>(null);
  const off = question.known && !question.on;

  // The platform has branches off: one place (a stale answer is dropped).
  useEffect(() => {
    if (off && onboardingStore.get().branches.multi) onboardingStore.setBranches({ multi: false });
  }, [off]);

  if (guard) return guard;
  // …and the screen just continues.
  if (off) return <Navigate to={ONB.address} replace />;
  // The config has not answered yet: nothing to ask until it does (no flash of the question).
  if (!question.known) return <Wizard screen="branches" testId="onb-business-branches-loading" state="loading" panel={panel} />;

  const submit = () => {
    if (branches.multi) {
      const name = branches.firstName.trim();
      if (!name || name.length > NAME_MAX) {
        setError(BRANCHES_Q.nameRequired);
        return;
      }
      onboardingStore.setBranches({ firstName: name });
    }
    navigate(ONB.address);
  };

  return (
    <Wizard
      screen="branches"
      testId="onb-business-branches"
      state={error ? 'validation' : 'default'}
      question={BRANCHES_Q.title}
      sub={BRANCHES_Q.sub}
      primary={<ContinueAction mobile={mobile} />}
      enterHint
      peek={BUSINESS.peekAddress}
      onSubmit={submit}
      panel={panel}
    >
      <div className="onb-listcard onb-branches" data-testid="onb-branches">
        <Toggle
          checked={branches.multi}
          onChange={(multi) => {
            setError(null);
            onboardingStore.setBranches({ multi });
          }}
          label={BRANCHES_Q.multi}
          description={BRANCHES_Q.multiSub}
        />
      </div>
      {branches.multi && (
        <>
          <label className="onb-field-label" htmlFor="onb-branch-name">
            {BRANCHES_Q.firstName}
          </label>
          <BigInput
            id="onb-branch-name"
            autoFocus
            data-testid="onb-branch-name"
            maxLength={NAME_MAX}
            value={branches.firstName}
            onChange={(v) => {
              setError(null);
              onboardingStore.setBranches({ firstName: v });
            }}
            error={error}
            autoComplete="off"
          />
          <Msg tone="info" testId="onb-branches-note">
            {BRANCHES_Q.note}
          </Msg>
        </>
      )}
    </Wizard>
  );
}
